import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Modal,
  Select,
  Tag,
  List,
  message,
  Spin,
  Space
} from 'antd';
import moment from 'moment';
import {
  apiGetDevicesInformationV2,
  apiGetLinkDown,
  apiCreateLinkDown,
  apiDeleteLinkDown,
  apiCheckDeviceExist
} from '@services/ManageDevices/DeviceServices';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  QrcodeOutlined,
  ScanOutlined,
  SendOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { showWarning, showSuccess, showError } from '@components/notification';

import * as Yup from 'yup';
import { extractQRCode, formatCurrency, formatNumber, isNullOrEmpty } from '@utils/utils';
import { Formik } from 'formik';
import { ScanQR } from './ScanQR';
import e from 'cors';

const { Option } = Select;
const { Text } = Typography;

const validationSchema = Yup.object().shape({
  imei: Yup.string().required('Vui lòng chọn thiết bị!'),
  firstValue: Yup.string().required('Vui lòng nhập số khối!'),
  secondValue: Yup.string()
    .max(3, 'Chỉ được nhập tối đa 3 ký tự')
    .matches(/^\d+$/, 'Chỉ được nhập số')
});

export default function SendDownLinkCommandV2() {
  const [deviceList, setDeviceList] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [cmd, setCmd] = useState({});
  const [historicalCmd, setHistoricalCmd] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(4);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [imei, setImei] = useState('');
  const [isDelete, setIsDelete] = useState(false);
  const [isCameraVisible, setIsCameraVisible] = useState(false);
  const [model, setModel] = useState('');

  const [initialValues, setInitialValues] = useState({
    imei: '',
    model: '',
    serial_no: '',
    meter_reading: '',
    firstValue: '',
    secondValue: ''
  });

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Xác nhận',
      content: 'Bạn có chắc chắn muốn xóa lệnh này?',
      okText: 'Xóa',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setIsDelete(true);
          await apiDeleteLinkDown(id);
          message.success('Xóa lệnh thành công');
          fetchSendLinkDown();
        } catch (error) {
          console.error(error);
          message.error('Có lỗi xảy ra, vui lòng liên hệ admin');
        } finally {
          setIsDelete(false);
        }
      }
    });
  };

  const handleSubmit = async (values) => {
    try {
      setIsSending(true);
      const firstFloatValue = values.firstValue.replace(/,/g, '') || '0';
      let secondFloatValue = values.secondValue.replace(/,/g, '') || '000';
      secondFloatValue = secondFloatValue.padEnd(3, '0');
      const payload = {
        cmd: {
          setMeterReading: `${firstFloatValue}${
            isNullOrEmpty(secondFloatValue) ? '000' : secondFloatValue
          }`
        },
        imei: values.imei,
        serial_no: values.serial_no
      };

      await apiCreateLinkDown(payload);
      message.success('Dữ liệu cài đặt đã được ghi nhận');
      fetchSendLinkDown();
      setInitialValues({ ...initialValues, firstValue: '', secondValue: '' });
    } catch (e) {
      console.error(e);
      message.error('Có lỗi xảy ra, vui lòng liên hệ admin');
    } finally {
      setIsSending(false);
    }
  };

  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleOpenModal = () => {
    // setIsModalVisible(true);
    setIsCameraVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setIsCameraVisible(false);
  };

  const checkDeviceExist = async (payload) => {
    try {
      const res = await apiCheckDeviceExist(payload);
      return res.data?.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const handleScanQrCode = async (qrCodeValue) => {
    // Simulate selecting a device based on the scanned QR code
    let extractQrCodeResult = extractQRCode(qrCodeValue);
    if (!extractQrCodeResult?.imei || !extractQrCodeResult?.serialNumber) {
      // extract from link
      const lowerCaseQrCodeValue = qrCodeValue.toLowerCase();
      const serialNumber = lowerCaseQrCodeValue.split('sn=')[1];
      if (serialNumber) {
        extractQrCodeResult.serialNumber = serialNumber;
      } else {
        message.error('Mã QR không hợp lệ');
        return;
      }
    }
    // check device exist
    const checkResult = await checkDeviceExist({
      imei: extractQrCodeResult?.imei ?? undefined,
      serial_number: extractQrCodeResult?.serialNumber ?? undefined
    });
    if (!checkResult) {
      message.error('Thiết bị bạn quét không tồn tại trên hện thống!');
      return;
    }
    message.success('Quét thành công!');
    setInitialValues({
      ...initialValues,
      imei: checkResult?.imei,
      serial_no: checkResult?.serial_no,
      meter_reading: checkResult?.meter_reading
    });
  };

  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const fetchDevices = async () => {
    try {
      const res = await apiGetDevicesInformationV2({ page: 1, size: 2000 });
      setDeviceList(
        res.data?.data?.map((item) => ({
          label: `IMEI:${item?.imei} S/N:${item?.serial_no}`,
          value: item?.imei,
          ...item
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSendLinkDown = async () => {
    try {
      setLoading(true);
      const res = await apiGetLinkDown({
        page: currentPage,
        size: pageSize,
        type: 'meter_reading',
        keyWord: imei ?? undefined
      });
      setHistoricalCmd(
        res.data?.data?.map((item, index) => ({
          ...item
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  useEffect(() => {
    fetchSendLinkDown();
  }, [currentPage, pageSize, imei]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSendLinkDown();
    }, 10000);

    return () => clearInterval(interval);
  }, [imei, currentPage, pageSize]);

  return (
    <div style={{ padding: '12px' }}>
      <Modal
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        bodyStyle={{ padding: 0 }}></Modal>
      {/* add form */}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => {
          useEffect(() => {
            setFieldValue('imei', initialValues?.imei);
            setFieldValue('serial_no', initialValues?.serial_no);
            setFieldValue('meter_reading', initialValues?.meter_reading);
            setImei(initialValues?.imei);
            setModel(initialValues?.model);
          }, [initialValues]);

          return (
            <>
              <div>
                <span style={{ color: '#0284c7', fontWeight: 500 }}>Chọn thiết bị *</span>
              </div>
              <Form.Item
                validateStatus={touched.imei && errors.imei ? 'error' : ''}
                help={touched.imei && errors.imei ? errors.imei : null}
                style={{ marginBottom: 0 }} // Prevent extra spacing
              >
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    style={{
                      flex: 1,
                      height: '40px',
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0
                    }}
                    placeholder="Chọn thiết bị hoặc nhấn scan QR code"
                    value={values.imei || null}
                    onChange={(value, option) => {
                      setFieldValue('imei', value);
                      setFieldValue('serial_no', option.serial_no);
                      setFieldValue('meter_reading', option.meter_reading);
                      setImei(value);
                      setModel(option?.model);
                    }}
                    options={deviceList}
                  />
                  <Button
                    type="primary"
                    icon={<QrcodeOutlined size={20} />} // Adjust icon size
                    style={{
                      height: '40px',
                      width: '40px',
                      marginLeft: '-1px',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      color: '#FFF',
                      backgroundColor: '#0284c7'
                    }}
                    onClick={handleOpenModal}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <ScanQR
                    onChange={handleScanQrCode}
                    isCameraVisible={isCameraVisible}
                    setIsCameraVisible={setIsCameraVisible}
                    setIsModalVisible={setIsModalVisible}
                  />
                </div>
              </Form.Item>
              <Text
                style={{
                  fontStyle: 'italic',
                  fontSize: '14px',
                  color: 'green',
                  paddingTop: '12px'
                }}>
                {`Model: ${model ? model : '-'}`}
              </Text>
              <br />
              {values.imei && (
                <Text
                  style={{
                    fontStyle: 'italic',
                    fontSize: '14px',
                    color: 'green',
                    paddingTop: '12px'
                  }}>
                  {`Chỉ số đồng hồ: ${
                    values.meter_reading
                      ? formatCurrency((values.meter_reading / 1000).toFixed(3), 3)
                      : '-'
                  }`}
                </Text>
              )}
              <div style={{ marginTop: '16px' }}>
                <span style={{ color: '#0284c7', fontWeight: 500 }}>Nhập chỉ số đồng hồ *</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Form.Item
                  label="Số khối (m3)"
                  validateStatus={touched.firstValue && errors.firstValue ? 'error' : ''}
                  help={touched.firstValue && errors.firstValue ? errors.firstValue : null}
                  style={{ flex: 1 }}>
                  <Input
                    style={{ height: '40px' }}
                    placeholder="Nhập số khối"
                    value={values.firstValue}
                    onChange={(e) => setFieldValue('firstValue', formatNumber(e.target.value))}
                  />
                </Form.Item>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Text style={{ fontSize: '24px', marginBottom: '20px' }}>.</Text>
                </div>
                <Form.Item
                  label="Số lít (L)"
                  validateStatus={touched.secondValue && errors.secondValue ? 'error' : ''}
                  help={touched.secondValue && errors.secondValue ? errors.secondValue : null}
                  style={{ flex: 1 }}>
                  <Input
                    placeholder="Nhập số lít"
                    style={{ height: '40px' }}
                    value={values.secondValue}
                    onChange={(e) => setFieldValue('secondValue', formatNumber(e.target.value))}
                  />
                </Form.Item>
              </div>
              {/* Instructions */}
              <div style={{ marginTop: '0px' }}>
                <div>
                  <span style={{ color: '#0284c7' }}>Hướng dẫn nhập chỉ số:</span>
                </div>
                <div style={{ fontStyle: 'italic', fontSize: '12px', color: 'gray' }}>
                  <div>Ví dụ: Chỉ số đồng hồ đang hiển thị là: 12345.6789</div>
                  <span>Nhập chỉ số đồng hồ như sau:</span>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    <li>Số khối (m3): 12345</li>
                    <li>Số lít (L): 678 hoặc làm tròn 679 (Không nhập mặc định là 0)</li>
                  </ul>
                </div>
              </div>

              <Button
                type="primary"
                onClick={handleSubmit}
                loading={isSending}
                style={{
                  marginTop: '20px',
                  marginBottom: '20px',
                  height: '40px',
                  backgroundColor: '#0284c7',
                  width: '100%'
                }}>
                Gửi lệnh
              </Button>
              <div style={{ overflowY: 'auto' }}>
                <Text
                  color="#2D9764"
                  style={{ fontSize: '16px', marginTop: '16px', fontWeight: 500, marginBottom: 0 }}>
                  Lệnh gần đây
                </Text>
                <List
                  dataSource={historicalCmd}
                  // loading={loading}
                  renderItem={(item) => {
                    const now = new Date();
                    const sentAt = new Date(item?.sent_at);
                    const isExpired = now.getTime() - 60000 > sentAt.getTime();
                    return (
                      <List.Item>
                        <Card bodyStyle={{ padding: 8 }} style={{ width: '100%', padding: 0 }}>
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Text color="#2D9764" style={{ fontSize: '14px', fontWeight: 500 }}>
                              IMEI:{item.imei} S/N: {item.serial_no}
                            </Text>
                            <Text
                              color="#2D9764"
                              style={{
                                fontSize: '14px',
                                fontWeight: 400
                              }}>{`Chỉ số set: ${formatCurrency(
                              (JSON.parse(item?.cmd).setMeterReading / 1000).toFixed(3),
                              3
                            )}`}</Text>
                            <Space>
                              {item?.is_sent === 2 ? (
                                <Tag icon={<CheckCircleOutlined />} color="success">
                                  Đã gửi lúc {moment(item?.sent_at).format('HH:mm DD/MM/YYYY')}
                                </Tag>
                              ) : item?.is_sent === 1 ? (
                                <>
                                  {isExpired ? (
                                    <Tag icon={<MinusCircleOutlined />} color="red">
                                      Gửi thất bại
                                    </Tag>
                                  ) : (
                                    <Tag icon={<SyncOutlined spin />} color="processing">
                                      Đang gửi
                                    </Tag>
                                  )}
                                </>
                              ) : (
                                <Tag icon={<CloseCircleOutlined />} color="error">
                                  Chưa gửi
                                </Tag>
                              )}
                            </Space>
                            <div
                              style={{
                                position: 'relative',
                                display: 'inline-block',
                                width: '100%'
                              }}>
                              <Space>
                                <Text color="#2D9764" style={{ fontSize: '14px', fontWeight: 400 }}>
                                  Tạo lúc {moment(item.created_at).format('DD/MM/YYYY HH:mm:ss')}
                                </Text>
                              </Space>
                              {item?.is_sent === 0 && (
                                <Button
                                  type="text"
                                  icon={<DeleteOutlined />}
                                  danger
                                  onClick={() => handleDelete(Number(item?.id))}
                                  loading={isDelete}
                                  style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    padding: '6px',
                                    backgroundColor: '#E4E4E7',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                />
                              )}
                            </div>
                          </Space>
                        </Card>
                      </List.Item>
                    );
                  }}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    onChange: (page) => setCurrentPage(page)
                  }}
                />
              </div>
            </>
          );
        }}
      </Formik>
      {/*  */}
    </div>
  );
}
